import { ref } from 'vue';

/**
 * Unified QA request helper (P2-FE-002):
 * - latest request wins (stale responses ignored)
 * - optional AbortController when caller forwards signal
 * - loading / error / empty flags
 */
export function useQaRequest() {
  const isLoading = ref(false);
  const error = ref(null);
  const isEmpty = ref(false);
  let sequence = 0;
  let controller = null;

  const run = async executor => {
    const mySequence = ++sequence;
    if (controller) controller.abort();
    controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    isLoading.value = true;
    error.value = null;

    try {
      const result = await executor({
        signal: controller?.signal,
        sequence: mySequence,
        isCurrent: () => mySequence === sequence,
      });
      if (mySequence !== sequence) return null;
      if (Array.isArray(result)) {
        isEmpty.value = result.length === 0;
      } else if (
        result &&
        typeof result === 'object' &&
        Array.isArray(result.items)
      ) {
        isEmpty.value = result.items.length === 0;
      } else {
        isEmpty.value = result == null;
      }
      return result;
    } catch (err) {
      if (mySequence !== sequence) return null;
      if (
        err?.name === 'CanceledError' ||
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED'
      ) {
        return null;
      }
      error.value = err;
      throw err;
    } finally {
      if (mySequence === sequence) {
        isLoading.value = false;
      }
    }
  };

  const reset = () => {
    sequence += 1;
    if (controller) controller.abort();
    isLoading.value = false;
    error.value = null;
    isEmpty.value = false;
  };

  return {
    isLoading,
    error,
    isEmpty,
    run,
    reset,
  };
}
